import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { SERVICES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Please enter a valid email address.";
  if (!/^[0-9+()\s-]{7,20}$/.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!f.service) e.service = "Please choose a service.";
  if (f.message.trim().length < 10) e.message = "Please tell us a little more (at least 10 characters).";
  return e;
};

const Field = ({ label, name, error, children }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">{label}</span>
    {children}
    {error && <span data-testid={`error-${name}`} className="mt-2 block text-sm text-red-400">{error}</span>}
  </label>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/enquiries`, { ...form, phone: form.phone.trim(), name: form.name.trim() });
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center border border-[#1E9BFF]/40 bg-[#1E9BFF]/5 p-10">
        <CheckCircle2 className="h-12 w-12 text-[#1E9BFF]" />
        <h3 className="mt-6 font-display text-3xl font-bold uppercase">Enquiry Received</h3>
        <p className="mt-3 text-neutral-300">Thanks for getting in touch. A member of our team will be back to you shortly.</p>
        <button data-testid="contact-send-another" onClick={() => setStatus("idle")} className="btn-outline mt-8">Send Another Enquiry</button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-6 border border-white/10 bg-white/[0.02] p-6 sm:p-10">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name" name="name" error={errors.name}><input data-testid="contact-name-input" value={form.name} onChange={set("name")} className="field" placeholder="John Smith" /></Field>
        <Field label="Phone" name="phone" error={errors.phone}><input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} className="field" placeholder="07700 900000" /></Field>
      </div>
      <Field label="Email" name="email" error={errors.email}><input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@example.com" /></Field>
      <Field label="Service Required" name="service" error={errors.service}>
        <select data-testid="contact-service-select" value={form.service} onChange={set("service")} className="field appearance-none">
          <option value="" disabled>Select a service</option>
          {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
        </select>
      </Field>
      <Field label="Message" name="message" error={errors.message}><textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} className="field resize-none" placeholder="Tell us about the problem or project..." /></Field>
      {status === "error" && (
        <div data-testid="contact-error" className="flex items-center gap-3 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
          <AlertCircle className="h-5 w-5 shrink-0" /> Something went wrong sending your enquiry. Please try again or call us directly.
        </div>
      )}
      <button data-testid="contact-submit-button" type="submit" disabled={status === "loading"} className="btn-primary w-full justify-center py-4 text-base disabled:opacity-60">
        {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {status === "loading" ? "Sending..." : "Send Enquiry"}
      </button>
    </form>
  );
};
